/**
 * Raptor Predator Frontend - Policy Store
 * 
 * Manages policy data, conflicts, guardrails, and firewall profiles.
 * Supports AI-assisted policy creation and conflict detection.
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type {
  Policy,
  PolicyType,
  PolicyRule,
  PolicyConflict,
  Guardrail,
  FirewallProfile,
  FirewallRule,
  CCLRule,
  VirtualNetwork,
} from '../types';

// ============================================================================
// Types
// ============================================================================

interface PolicyFilters {
  types: PolicyType[];
  enabled: boolean | null;
  searchQuery: string;
}

interface PolicyStats {
  total: number;
  enabled: number;
  disabled: number;
  byType: Record<PolicyType, number>;
  conflictCount: number;
}

interface PolicyState {
  // Policies
  policies: Map<string, Policy>;
  
  // Conflicts
  conflicts: Map<string, PolicyConflict>;
  
  // Guardrails
  guardrails: Map<string, Guardrail>;
  
  // Firewall profiles
  firewallProfiles: Map<string, FirewallProfile>;
  
  // CCL Rules
  cclRules: Map<string, CCLRule>;
  
  // Virtual Networks
  virtualNetworks: Map<string, VirtualNetwork>;
  
  // Filters
  filters: PolicyFilters;
  
  // Stats
  stats: PolicyStats;
  
  // Selection
  selectedPolicyId: string | null;
  editingPolicyId: string | null;
  
  // Draft policy for creation
  draftPolicy: Partial<Policy> | null;
  
  // Loading state
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  lastUpdate: number;
}

interface PolicyActions {
  // Policy CRUD
  addPolicy: (policy: Policy) => void;
  updatePolicy: (id: string, updates: Partial<Policy>) => void;
  deletePolicy: (id: string) => void;
  setPolicies: (policies: Policy[]) => void;
  
  // Policy rules
  addRule: (policyId: string, rule: PolicyRule) => void;
  updateRule: (policyId: string, ruleId: string, updates: Partial<PolicyRule>) => void;
  deleteRule: (policyId: string, ruleId: string) => void;
  
  // Conflicts
  addConflict: (conflict: PolicyConflict) => void;
  resolveConflict: (id: string, resolution: PolicyConflict['resolution']) => void;
  clearConflicts: () => void;
  detectConflicts: () => void;
  
  // Guardrails
  addGuardrail: (guardrail: Guardrail) => void;
  updateGuardrail: (id: string, updates: Partial<Guardrail>) => void;
  deleteGuardrail: (id: string) => void;
  setGuardrails: (guardrails: Guardrail[]) => void;
  
  // Firewall profiles
  addFirewallProfile: (profile: FirewallProfile) => void;
  updateFirewallProfile: (id: string, updates: Partial<FirewallProfile>) => void;
  deleteFirewallProfile: (id: string) => void;
  setFirewallProfiles: (profiles: FirewallProfile[]) => void;
  
  // Firewall rules
  addFirewallRule: (profileId: string, rule: FirewallRule) => void;
  updateFirewallRule: (profileId: string, ruleId: string, updates: Partial<FirewallRule>) => void;
  deleteFirewallRule: (profileId: string, ruleId: string) => void;
  
  // CCL Rules
  addCCLRule: (rule: CCLRule) => void;
  updateCCLRule: (id: string, updates: Partial<CCLRule>) => void;
  deleteCCLRule: (id: string) => void;
  setCCLRules: (rules: CCLRule[]) => void;
  
  // Virtual Networks
  addVirtualNetwork: (network: VirtualNetwork) => void;
  updateVirtualNetwork: (id: string, updates: Partial<VirtualNetwork>) => void;
  deleteVirtualNetwork: (id: string) => void;
  setVirtualNetworks: (networks: VirtualNetwork[]) => void;
  addDeviceToNetwork: (networkId: string, deviceId: string) => void;
  removeDeviceFromNetwork: (networkId: string, deviceId: string) => void;
  
  // Selection
  selectPolicy: (id: string | null) => void;
  setEditingPolicy: (id: string | null) => void;
  
  // Draft
  setDraftPolicy: (draft: Partial<Policy> | null) => void;
  updateDraftPolicy: (updates: Partial<Policy>) => void;
  saveDraft: () => void;
  discardDraft: () => void;
  
  // Filtering
  setFilters: (filters: Partial<PolicyFilters>) => void;
  resetFilters: () => void;
  
  // State management
  setLoading: (loading: boolean) => void;
  setSaving: (saving: boolean) => void;
  setError: (error: string | null) => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialFilters: PolicyFilters = {
  types: [],
  enabled: null,
  searchQuery: '',
};

const initialStats: PolicyStats = {
  total: 0,
  enabled: 0,
  disabled: 0,
  byType: {} as Record<PolicyType, number>,
  conflictCount: 0,
};

const initialState: PolicyState = {
  policies: new Map(),
  conflicts: new Map(),
  guardrails: new Map(),
  firewallProfiles: new Map(),
  cclRules: new Map(),
  virtualNetworks: new Map(),
  filters: initialFilters,
  stats: initialStats,
  selectedPolicyId: null,
  editingPolicyId: null,
  draftPolicy: null,
  isLoading: false,
  isSaving: false,
  error: null,
  lastUpdate: Date.now(),
};

// ============================================================================
// Helper Functions
// ============================================================================

function computeStats(policies: Map<string, Policy>, conflicts: Map<string, PolicyConflict>): PolicyStats {
  const stats: PolicyStats = {
    total: policies.size,
    enabled: 0,
    disabled: 0,
    byType: {} as Record<PolicyType, number>,
    conflictCount: conflicts.size,
  };
  
  for (const policy of policies.values()) {
    if (policy.enabled) {
      stats.enabled++;
    } else {
      stats.disabled++;
    }
    
    stats.byType[policy.type] = (stats.byType[policy.type] || 0) + 1;
  }
  
  return stats;
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// ============================================================================
// Store Creation
// ============================================================================

export const usePolicyStore = create<PolicyState & PolicyActions>()(
  devtools(
    subscribeWithSelector(
      immer((set, _get) => ({
        ...initialState,
        
        // Policy CRUD
        addPolicy: (policy) => { set((state) => {
          state.policies.set(policy.id, policy);
          state.stats = computeStats(state.policies, state.conflicts);
          state.lastUpdate = Date.now();
        }); },
        
        updatePolicy: (id, updates) => { set((state) => {
          const policy = state.policies.get(id);
          if (policy) {
            Object.assign(policy, updates, { updatedAt: new Date() });
            state.stats = computeStats(state.policies, state.conflicts);
            state.lastUpdate = Date.now();
          }
        }); },
        
        deletePolicy: (id) => { set((state) => {
          state.policies.delete(id);
          
          // Remove related conflicts
          for (const [conflictId, conflict] of state.conflicts) {
            if (conflict.policies.includes(id)) {
              state.conflicts.delete(conflictId);
            }
          }
          
          state.stats = computeStats(state.policies, state.conflicts);
          state.lastUpdate = Date.now();
        }); },
        
        setPolicies: (policies) => { set((state) => {
          state.policies.clear();
          for (const policy of policies) {
            state.policies.set(policy.id, policy);
          }
          state.stats = computeStats(state.policies, state.conflicts);
          state.lastUpdate = Date.now();
        }); },
        
        // Policy rules
        addRule: (policyId, rule) => { set((state) => {
          const policy = state.policies.get(policyId);
          if (policy) {
            policy.rules.push(rule);
            policy.updatedAt = new Date();
            state.lastUpdate = Date.now();
          }
        }); },
        
        updateRule: (policyId, ruleId, updates) => { set((state) => {
          const policy = state.policies.get(policyId);
          if (policy) {
            const rule = policy.rules.find(r => r.id === ruleId);
            if (rule) {
              Object.assign(rule, updates);
              policy.updatedAt = new Date();
              state.lastUpdate = Date.now();
            }
          }
        }); },
        
        deleteRule: (policyId, ruleId) => { set((state) => {
          const policy = state.policies.get(policyId);
          if (policy) {
            policy.rules = policy.rules.filter(r => r.id !== ruleId);
            policy.updatedAt = new Date();
            state.lastUpdate = Date.now();
          }
        }); },
        
        // Conflicts
        addConflict: (conflict) => { set((state) => {
          state.conflicts.set(conflict.id, conflict);
          state.stats = computeStats(state.policies, state.conflicts);
        }); },
        
        resolveConflict: (id, resolution) => { set((state) => {
          const conflict = state.conflicts.get(id);
          if (conflict && resolution) {
            conflict.resolution = resolution;
          }
        }); },
        
        clearConflicts: () => { set((state) => {
          state.conflicts.clear();
          state.stats = computeStats(state.policies, state.conflicts);
        }); },
        
        detectConflicts: () => { set((state) => {
          // Clear existing conflicts
          state.conflicts.clear();
          
          // Compare all policy pairs
          const policyList = Array.from(state.policies.values());
          
          for (let i = 0; i < policyList.length; i++) {
            for (let j = i + 1; j < policyList.length; j++) {
              const p1 = policyList[i];
              const p2 = policyList[j];
              
              if (!p1 || !p2) {continue;}
              
              // Check for scope overlap
              const hasOverlap = 
                (p1.scope.targetType === 'all' || p2.scope.targetType === 'all') ||
                (p1.scope.targetIds?.some(id => p2.scope.targetIds?.includes(id)));
              
              if (hasOverlap) {
                // Check for contradicting actions in rules
                for (const r1 of p1.rules) {
                  for (const r2 of p2.rules) {
                    if (r1.action !== r2.action) {
                      // Potential conflict
                      const conflict: PolicyConflict = {
                        id: generateId(),
                        policies: [p1.id, p2.id],
                        conflictType: 'contradicting-actions',
                        description: `Policy "${p1.name}" (${r1.action}) conflicts with "${p2.name}" (${r2.action})`,
                        severity: 'medium',
                      };
                      state.conflicts.set(conflict.id, conflict);
                    }
                  }
                }
              }
            }
          }
          
          state.stats = computeStats(state.policies, state.conflicts);
        }); },
        
        // Guardrails
        addGuardrail: (guardrail) => { set((state) => {
          state.guardrails.set(guardrail.id, guardrail);
          state.lastUpdate = Date.now();
        }); },
        
        updateGuardrail: (id, updates) => { set((state) => {
          const guardrail = state.guardrails.get(id);
          if (guardrail) {
            Object.assign(guardrail, updates);
            state.lastUpdate = Date.now();
          }
        }); },
        
        deleteGuardrail: (id) => { set((state) => {
          state.guardrails.delete(id);
          state.lastUpdate = Date.now();
        }); },
        
        setGuardrails: (guardrails) => { set((state) => {
          state.guardrails.clear();
          for (const guardrail of guardrails) {
            state.guardrails.set(guardrail.id, guardrail);
          }
          state.lastUpdate = Date.now();
        }); },
        
        // Firewall profiles
        addFirewallProfile: (profile) => { set((state) => {
          state.firewallProfiles.set(profile.id, profile);
          state.lastUpdate = Date.now();
        }); },
        
        updateFirewallProfile: (id, updates) => { set((state) => {
          const profile = state.firewallProfiles.get(id);
          if (profile) {
            Object.assign(profile, updates, { updatedAt: new Date() });
            state.lastUpdate = Date.now();
          }
        }); },
        
        deleteFirewallProfile: (id) => { set((state) => {
          state.firewallProfiles.delete(id);
          state.lastUpdate = Date.now();
        }); },
        
        setFirewallProfiles: (profiles) => { set((state) => {
          state.firewallProfiles.clear();
          for (const profile of profiles) {
            state.firewallProfiles.set(profile.id, profile);
          }
          state.lastUpdate = Date.now();
        }); },
        
        // Firewall rules
        addFirewallRule: (profileId, rule) => { set((state) => {
          const profile = state.firewallProfiles.get(profileId);
          if (profile) {
            profile.rules.push(rule);
            profile.updatedAt = new Date();
            state.lastUpdate = Date.now();
          }
        }); },
        
        updateFirewallRule: (profileId, ruleId, updates) => { set((state) => {
          const profile = state.firewallProfiles.get(profileId);
          if (profile) {
            const rule = profile.rules.find(r => r.id === ruleId);
            if (rule) {
              Object.assign(rule, updates);
              profile.updatedAt = new Date();
              state.lastUpdate = Date.now();
            }
          }
        }); },
        
        deleteFirewallRule: (profileId, ruleId) => { set((state) => {
          const profile = state.firewallProfiles.get(profileId);
          if (profile) {
            profile.rules = profile.rules.filter(r => r.id !== ruleId);
            profile.updatedAt = new Date();
            state.lastUpdate = Date.now();
          }
        }); },
        
        // CCL Rules
        addCCLRule: (rule) => { set((state) => {
          state.cclRules.set(rule.id, rule);
          state.lastUpdate = Date.now();
        }); },
        
        updateCCLRule: (id, updates) => { set((state) => {
          const rule = state.cclRules.get(id);
          if (rule) {
            Object.assign(rule, updates);
            state.lastUpdate = Date.now();
          }
        }); },
        
        deleteCCLRule: (id) => { set((state) => {
          state.cclRules.delete(id);
          state.lastUpdate = Date.now();
        }); },
        
        setCCLRules: (rules) => { set((state) => {
          state.cclRules.clear();
          for (const rule of rules) {
            state.cclRules.set(rule.id, rule);
          }
          state.lastUpdate = Date.now();
        }); },
        
        // Virtual Networks
        addVirtualNetwork: (network) => { set((state) => {
          state.virtualNetworks.set(network.id, network);
          state.lastUpdate = Date.now();
        }); },
        
        updateVirtualNetwork: (id, updates) => { set((state) => {
          const network = state.virtualNetworks.get(id);
          if (network) {
            Object.assign(network, updates, { updatedAt: new Date() });
            state.lastUpdate = Date.now();
          }
        }); },
        
        deleteVirtualNetwork: (id) => { set((state) => {
          state.virtualNetworks.delete(id);
          state.lastUpdate = Date.now();
        }); },
        
        setVirtualNetworks: (networks) => { set((state) => {
          state.virtualNetworks.clear();
          for (const network of networks) {
            state.virtualNetworks.set(network.id, network);
          }
          state.lastUpdate = Date.now();
        }); },
        
        addDeviceToNetwork: (networkId, deviceId) => { set((state) => {
          const network = state.virtualNetworks.get(networkId);
          if (network && !network.memberIds.includes(deviceId)) {
            network.memberIds.push(deviceId);
            network.updatedAt = new Date();
            state.lastUpdate = Date.now();
          }
        }); },
        
        removeDeviceFromNetwork: (networkId, deviceId) => { set((state) => {
          const network = state.virtualNetworks.get(networkId);
          if (network) {
            network.memberIds = network.memberIds.filter(id => id !== deviceId);
            network.updatedAt = new Date();
            state.lastUpdate = Date.now();
          }
        }); },
        
        // Selection
        selectPolicy: (id) => { set((state) => {
          state.selectedPolicyId = id;
        }); },
        
        setEditingPolicy: (id) => { set((state) => {
          state.editingPolicyId = id;
        }); },
        
        // Draft
        setDraftPolicy: (draft) => { set((state) => {
          state.draftPolicy = draft;
        }); },
        
        updateDraftPolicy: (updates) => { set((state) => {
          if (state.draftPolicy) {
            Object.assign(state.draftPolicy, updates);
          }
        }); },
        
        saveDraft: () => { set((state) => {
          if (state.draftPolicy?.id) {
            const policy: Policy = {
              id: state.draftPolicy.id,
              name: state.draftPolicy.name ?? 'Untitled Policy',
              description: state.draftPolicy.description ?? '',
              type: state.draftPolicy.type ?? 'custom',
              rules: state.draftPolicy.rules ?? [],
              enabled: state.draftPolicy.enabled ?? false,
              priority: state.draftPolicy.priority ?? 100,
              scope: state.draftPolicy.scope ?? { targetType: 'all' },
              createdAt: state.draftPolicy.createdAt ?? new Date(),
              updatedAt: new Date(),
              createdBy: state.draftPolicy.createdBy ?? 'system',
              version: state.draftPolicy.version ?? 1,
            };
            state.policies.set(policy.id, policy);
            state.draftPolicy = null;
            state.stats = computeStats(state.policies, state.conflicts);
            state.lastUpdate = Date.now();
          }
        }); },
        
        discardDraft: () => { set((state) => {
          state.draftPolicy = null;
        }); },
        
        // Filtering
        setFilters: (filters) => { set((state) => {
          Object.assign(state.filters, filters);
        }); },
        
        resetFilters: () => { set((state) => {
          state.filters = initialFilters;
        }); },
        
        // State management
        setLoading: (loading) => { set((state) => {
          state.isLoading = loading;
        }); },
        
        setSaving: (saving) => { set((state) => {
          state.isSaving = saving;
        }); },
        
        setError: (error) => { set((state) => {
          state.error = error;
          state.isLoading = false;
          state.isSaving = false;
        }); },
      }))
    ),
    { name: 'policy-store' }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectPolicies = (state: PolicyState & PolicyActions) => state.policies;
export const selectPolicyStats = (state: PolicyState & PolicyActions) => state.stats;
export const selectConflicts = (state: PolicyState & PolicyActions) => state.conflicts;
export const selectGuardrails = (state: PolicyState & PolicyActions) => state.guardrails;
export const selectFirewallProfiles = (state: PolicyState & PolicyActions) => state.firewallProfiles;
export const selectCCLRules = (state: PolicyState & PolicyActions) => state.cclRules;
export const selectVirtualNetworks = (state: PolicyState & PolicyActions) => state.virtualNetworks;
export const selectSelectedPolicy = (state: PolicyState & PolicyActions) =>
  state.selectedPolicyId ? state.policies.get(state.selectedPolicyId) : null;
export const selectDraftPolicy = (state: PolicyState & PolicyActions) => state.draftPolicy;
export const selectIsLoading = (state: PolicyState & PolicyActions) => state.isLoading;
export const selectIsSaving = (state: PolicyState & PolicyActions) => state.isSaving;

// ============================================================================
// Filtered Policies Selector
// ============================================================================

export const selectFilteredPolicies = (state: PolicyState & PolicyActions): Policy[] => {
  const { policies, filters } = state;
  const result: Policy[] = [];
  
  for (const policy of policies.values()) {
    // Type filter
    if (filters.types.length > 0 && !filters.types.includes(policy.type)) {
      continue;
    }
    
    // Enabled filter
    if (filters.enabled !== null && policy.enabled !== filters.enabled) {
      continue;
    }
    
    // Search query
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      const matchesName = policy.name.toLowerCase().includes(query);
      const matchesDescription = policy.description.toLowerCase().includes(query);
      
      if (!matchesName && !matchesDescription) {
        continue;
      }
    }
    
    result.push(policy);
  }
  
  // Sort by priority (lower = higher priority)
  result.sort((a, b) => a.priority - b.priority);
  
  return result;
};

